import clsx from 'clsx';
import Link from 'next/link';
import { useContext, useState } from 'react';
import { ProductDummy } from 'src/components/pages/top/ProductDummy';
import { NextImage } from 'src/components/shared/elements/NextImage';
import { ReadMoreButton } from 'src/components/shared/elements/buttons/ReadMoreButton';
import { MainContainer } from 'src/components/shared/layouts/MainContainer';
import { CATEGORY_TYPE } from 'src/constants/category';
import { useProductsByRanking } from 'src/hooks/useProducts';
import type { Product, ProductDetail } from 'src/models/product/schema';
import { BrandsContext } from 'src/stores/Brands';
import { CategoryContext } from 'src/stores/Category';
import { I18nContext } from 'src/stores/I18n';
import { brandName } from 'src/utils/brandName';
import { comma } from 'src/utils/comma';
import { mixpanel } from 'src/utils/mixpanel';
import styles from './index.module.scss';

export const RankingProducts: React.FC = () => {
  const [activeCategorySlug, setActiveCategorySlug] = useState<string>('all');
  const category = useContext(CategoryContext);
  const localeResources = useContext(I18nContext);
  const { products, isProductsLoading } = useProductsByRanking(
    { slug: activeCategorySlug, type: CATEGORY_TYPE.LARGE },
    8
  );
  const activeCategoryName = (slug: string): string => {
    const targetCategory = category.largeCategories.find((c) => c.slug === slug);
    if (targetCategory) {
      return targetCategory.name;
    } else {
      return localeResources.commons.all;
    }
  };
  const handleClick = (product: Product) => {
    mixpanel.onRankingProductClicked(
      activeCategorySlug,
      product.productId,
      product.middleCategoryId,
      product.largeCategoryId
    );
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>
        {activeCategoryName(activeCategorySlug) + ' ' + localeResources.ranking.titleName}
      </h2>
      <div className={styles.categories}>
        <div
          key={0}
          onClick={() => {
            mixpanel.onNaviRankingSectionClicked('all');
            setActiveCategorySlug('all');
          }}
          className={clsx(styles.category, activeCategorySlug === 'all' && styles.isActive)}
        >
          {localeResources.commons.all}
        </div>
        {category.largeCategories.map((category) => (
          <div
            key={category.largeCategoryId}
            onClick={() => {
              mixpanel.onNaviRankingSectionClicked(category.slug);
              setActiveCategorySlug(category.slug);
            }}
            className={clsx(styles.category, activeCategorySlug === category.slug && styles.isActive)}
          >
            {category.name}
          </div>
        ))}
      </div>
      <MainContainer>
        <div className={styles.products}>
          {isProductsLoading &&
            [0, 1, 2, 3].map((_, index) => {
              return (
                <div key={index}>
                  <ProductDummy id={String(index)} />
                </div>
              );
            })}
          {products &&
            products.map((product, index) => {
              return (
                <div key={index} onClick={() => handleClick(product)}>
                  <RankingProduct product={product} index={index} />
                </div>
              );
            })}
        </div>
        <ReadMoreButton
          as={'/ranking'}
          href={{
            pathname: '/ranking',
            query: { slug: activeCategorySlug }
          }}
          text={localeResources.categoryRanking.seeMore}
        />
      </MainContainer>
    </div>
  );
};

type RankingProductProps = {
  product: ProductDetail;
  index: number;
};

const RankingProduct: React.FC<RankingProductProps> = ({ product, index }) => {
  const brands = useContext(BrandsContext);

  const rankingStyles = () => {
    switch (index + 1) {
      case 1:
        return styles.first;
      case 2:
        return styles.second;
      case 3:
        return styles.third;
      default:
        return;
    }
  };

  return (
    <Link href={`/products/${product.productId}`}>
      <a aria-label={product.name}>
        <div className={styles.productContainer}>
          <div className={styles.imageWrapper}>
            <div className={clsx(styles.ranking, rankingStyles())}>{index + 1}</div>
            <div className={styles.image}>
              <NextImage
                src={product.images[0]}
                width={300}
                height={300}
                layout="responsive"
                objectFit="contain"
                alt={product.name}
              />
            </div>
          </div>
          <div className={styles.content}>
            <div>
              <span className={styles.brand}>{brandName(product, brands)}</span>
              <span className={styles.name}>{product.name}</span>
            </div>
            <div className={styles.price}>
              <div className={styles.strong}>
                <span className={styles.currency}>{product.currencySymbol}</span>
                {comma(product.exchangeRateAdjustedPrice)}
              </div>
              <div className={styles.local}>
                JPY
                {comma(product.undiscountedPrice)}
              </div>
            </div>
          </div>
        </div>
      </a>
    </Link>
  );
};
